import {DOCUMENT} from '@angular/common';
import {
  EnvironmentProviders,
  inject,
  Injectable,
  InjectionToken,
  makeEnvironmentProviders,
  provideAppInitializer,
  signal,
} from '@angular/core';
import {
  DARK_MODE_STORAGE_KEY,
  DarkModePreference,
  resolveIsDark,
  resolveStoredPreference,
} from './dark-mode.utils';

export const DARK_MODE_DEFAULT = new InjectionToken<DarkModePreference>(
  'DARK_MODE_DEFAULT',
);

@Injectable({providedIn: 'root'})
export class DarkModeService {
  private readonly document = inject(DOCUMENT);
  private readonly defaultPreference = inject(DARK_MODE_DEFAULT, {
    optional: true,
  }) ?? 'light';
  private readonly media = this.document.defaultView?.matchMedia(
    '(prefers-color-scheme: dark)',
  );

  readonly preference = signal<DarkModePreference>('light');

  readonly isDark = signal(false);

  init(): void {
    const preference = resolveStoredPreference(
      localStorage.getItem(DARK_MODE_STORAGE_KEY),
      this.defaultPreference,
    );
    this.preference.set(preference);
    this.applyMode(preference);

    this.media?.addEventListener('change', () => {
      if (this.preference() !== 'system') return;
      this.applyMode(this.preference());
    });
  }

  setMode(mode: DarkModePreference): void {
    this.preference.set(mode);
    localStorage.setItem(DARK_MODE_STORAGE_KEY, mode);
    this.applyMode(mode);
  }

  private applyMode(mode: DarkModePreference): void {
    const dark = resolveIsDark(mode, this.media?.matches ?? false);
    this.isDark.set(dark);
    this.syncDom(dark);
  }

  private syncDom(dark: boolean): void {
    this.document.documentElement.classList.toggle('dark', dark);

    const darkStylesheet = this.document.getElementById(
      'ngZorroDarkTheme',
    ) as HTMLLinkElement | null;
    if (darkStylesheet) darkStylesheet.disabled = !dark;
  }
}

export function provideDarkMode(
  defaultPreference: DarkModePreference = 'light',
): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: DARK_MODE_DEFAULT, useValue: defaultPreference },
    provideAppInitializer(() => inject(DarkModeService).init()),
  ]);
}
